import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { nanoid } from "nanoid";

import {
  getCurrentRole,
  prepareLocation,
} from "components/UserCardList/helpers/userCard";
import ImageViewer from "components/ImageViewer";
import { getPurifiedParsedMarkDown } from "utils/markdown";
import { cn } from "utils";
import placeholderPicture from "images/profile-dummy-photo.png";
import MatchScoreBadge from "components/UserCardList/components/MatchScoreBadge";

const RankDetailsHeader = ({ hit, ideal, score, showProfileImage }) => {
  if (!hit) {
    return null;
  }

  const currentRole = getCurrentRole(hit);
  const name = `${hit.given_name} ${hit.family_name}`.trim();

  return (
    <header className="mb-6 flex items-start gap-4">
      {showProfileImage && (
        <div className="h-20 w-20 flex-none overflow-hidden rounded-lg">
          <ImageViewer
            objectKey={hit.headshotKey}
            width={80}
            responsive={false}
            placeholder={
              <img
                src={placeholderPicture}
                alt="placeholder"
                className="h-20 w-20"
              />
            }
          />
        </div>
      )}

      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 className="text-2xl font-bold text-slate-700">{name}</h2>
          {currentRole && (
            <>
              <span className="h-8 w-px bg-slate-300" />
              <p
                className="max-w-[420px] truncate text-lg text-slate-700"
                title={currentRole}
              >
                {currentRole}
              </p>
            </>
          )}
          <MatchScoreBadge ideal={ideal} score={score} className="h-8" />
        </div>

        <p className="text-base text-slate-700">
          {prepareLocation(hit.location) || hit.locale}
        </p>
      </div>
    </header>
  );
};

const RankDetails = ({
  content,
  isFirstIdeal,
  error,
  hit,
  ideal,
  score,
  showProfileImage = false,
  defaultOpen,
  className,
  variant,
}) => {
  const [open, setOpen] = useState(defaultOpen ?? isFirstIdeal);
  const [hasOverflow, setOverflow] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const boxRef = useRef(null);

  const errorMessage = "⚠️ Temporary error.";

  const COLLAPSED_HEIGHT = 135;

  useEffect(() => {
    if (defaultOpen || isFirstIdeal) {
      setOpen(true);
    }
  }, [defaultOpen, isFirstIdeal]);

  const isProfileVariant = variant === "profile";

  const renderStatsDetails = ({ items = [], title, className, bulleted }) => {
    return (
      <div className={className}>
        {title && (
          <p
            className={cn("mb-2 font-semibold", {
              "text-base text-slate-700": isProfileVariant,
              "text-lg": !isProfileVariant,
            })}
          >
            {title}
          </p>
        )}
        <ul className={cn("space-y-2", { "list-disc pl-6": bulleted })}>
          {items.length > 0 ? (
            items.map((item) => (
              <li
                key={nanoid()}
                // className="prose" // keeping same behabior prior to @tailwindcss/typography so not including prose class
                dangerouslySetInnerHTML={{
                  __html: getPurifiedParsedMarkDown(item || ""),
                }}
              />
            ))
          ) : (
            <li>None</li>
          )}
        </ul>
      </div>
    );
  };

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;

    const measure = () => {
      const h = el.scrollHeight;
      setOverflow(h > COLLAPSED_HEIGHT);

      setFullHeight(h);
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);

    resizeObserver.observe(el);
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [content, error]);

  return (
    <div className={cn("flex w-full flex-col gap-3", className)}>
      <RankDetailsHeader
        hit={hit}
        ideal={ideal}
        score={score}
        showProfileImage={showProfileImage}
      />

      <div
        ref={boxRef}
        style={{
          maxHeight: open ? `${fullHeight}px` : `${COLLAPSED_HEIGHT}px`,
        }}
        className={cn(
          "relative flex flex-col gap-y-4 overflow-hidden text-slate-700 transition-[max-height] duration-300 ease-in-out",
          {
            "gap-x-8 lg:flex-row": !isProfileVariant,
            "rounded-lg bg-violet-50 p-5 lg:grid lg:grid-cols-3 lg:gap-x-12":
              isProfileVariant,
          }
        )}
      >
        {renderStatsDetails({
          items: [error ? errorMessage : content?.explanation],
          title: "Summary",
          className: cn("w-full leading-7 text-base", {
            "lg:w-[40%] lg:flex-none": !isProfileVariant,
            "min-w-0": isProfileVariant,
          }),
        })}

        {!isProfileVariant && (
          <div className="h-px w-full bg-[#CBD5E1] lg:h-auto lg:min-h-full lg:w-px lg:self-stretch">
            <span className="text-[1px] opacity-0">-</span>
          </div>
        )}

        <div
          className={cn("flex w-full flex-col gap-y-4", {
            "gap-x-8 lg:flex-row": !isProfileVariant,
            contents: isProfileVariant,
          })}
        >
          {renderStatsDetails({
            items: content?.strengths,
            title: "Strengths",
            className: cn("w-full flex-auto leading-7 text-base", {
              "lg:flex-1": !isProfileVariant,
              "min-w-0": isProfileVariant,
            }),
            bulleted: true,
          })}

          {renderStatsDetails({
            items: content?.gaps,
            title: "Gaps",
            className: cn("w-full flex-auto leading-7 text-base", {
              "lg:flex-1": !isProfileVariant,
              "min-w-0": isProfileVariant,
            }),
            bulleted: true,
          })}
        </div>

        {!open && hasOverflow && (
          <div
            className={cn(
              "pointer-events-none absolute bottom-0 left-0 h-16 w-full bg-gradient-to-t to-transparent",
              {
                "from-white": !isProfileVariant,
                "from-violet-50": isProfileVariant,
              }
            )}
          />
        )}
      </div>

      {hasOverflow && (
        <button
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-x-1 text-base font-semibold text-[#0084C7] hover:underline"
          data-action="toggle-details"
          data-state={open ? "expanded" : "collapsed"}
          aria-expanded={open}
        >
          {open ? "Show less" : "Show more"}
          <span>{open ? <ChevronUp /> : <ChevronDown />}</span>
        </button>
      )}
    </div>
  );
};

export default RankDetails;
